/**
 * Social Feed — v2.0 LinkedIn-style 3-column layout.
 *
 * Production rebuild of /app/leadflow/feed using the social prototype's
 * polished outer shell + post cards, wired to the existing
 * socialFeedService and ProfileService. No backend changes.
 *
 *   • Left rail (lg+, sticky):  MiniProfileCard + SectionMenu
 *   • Main column:               ComposerCard + filters + 3-tab content
 *                                (Feed / My posts / Saved) + post stream
 *                                + Load more
 *   • Right rail (lg+):          Trending + People you may know +
 *                                Quick actions
 *   • Mobile:                    list stacks, ScrollTop FAB (frosted),
 *                                composer Dialog full-screen
 *
 * Drops the prototype's 4th tab (Drafts) — no drafts API on the backend.
 *
 * Anti-regression — every production behavior preserved:
 *   • markFeedViewed / fetchPendingConnectionsCount / fetchNewFeedPostsCount
 *     on mount via useNotifications (drives topbar badges)
 *   • LN gate Dialog for 403 responses with `ln_*` codes
 *   • Single-post deep-link via ?post=<id> (uses getPost)
 *   • Sort by recent / popular, post-type filter, paginated lists
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Image } from 'primereact/image';
import { Galleria } from 'primereact/galleria';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { ScrollTop } from 'primereact/scrolltop';

import { socialFeedService } from '../../services/socialFeedService';
import marketplaceService from '../../services/marketplaceService';
import ProfileService from '../../services/agentProfile/ProfileService';
import { useNotifications } from '../../contexts/NotificationContext';
import SideWidget from '../../components/agentProfile/SideWidget';

// ─── Constants ──────────────────────────────────────────────────────────
const POST_TYPE_META = {
    general:     null,
    article:     { label: 'Article',     icon: 'pi-file',       severity: 'info'    },
    achievement: { label: 'Achievement', icon: 'pi-trophy',     severity: 'warning' },
    lead_share:  { label: 'Lead share',  icon: 'pi-share-alt',  severity: 'success' },
    question:    { label: 'Question',    icon: 'pi-question',   severity: null      },
};

const VISIBILITY_META = {
    public:      { label: 'Public',                 icon: 'pi-globe' },
    connections: { label: 'Connections only',       icon: 'pi-users' },
    tenant:      { label: 'Within my organisation', icon: 'pi-building' },
};

/* Author role badge — rendered next to the post author's name when the
   backend includes `user_type`. Skipped silently when the field is absent
   so we never show empty pills. */
const USER_TYPE_BADGE = {
    loan_agent:           { label: 'Agent',      severity: 'success' },
    dsa:                  { label: 'DSA',        severity: 'info'    },
    financial_consultant: { label: 'Consultant', severity: 'info'    },
    insurance_agent:      { label: 'Insurance',  severity: 'warning' },
    investment_advisor:   { label: 'Advisor',    severity: 'info'    },
    tax_consultant:       { label: 'Tax',        severity: 'warning' },
    real_estate_agent:    { label: 'Realty',     severity: 'success' },
    bank:                 { label: 'Banker',     severity: 'warning' },
    nbfc:                 { label: 'NBFC',       severity: 'info'    },
};

/* "Show more" threshold for long post bodies. ~280 chars ≈ 4 lines on
   desktop; collapses anything longer behind a Show more / Show less Button. */
const POST_PREVIEW_CHARS = 280;

const SORT_OPTIONS = [
    { label: 'Recent',  value: 'recent' },
    { label: 'Popular', value: 'popular' },
];

const TYPE_FILTER_OPTIONS = [
    { label: 'All',          value: '' },
    { label: 'General',      value: 'general' },
    { label: 'Lead shares',  value: 'lead_share' },
    { label: 'Articles',     value: 'article' },
    { label: 'Achievements', value: 'achievement' },
    { label: 'Questions',    value: 'question' },
];

const COLOR_PALETTE = ['blue', 'indigo', 'purple', 'pink', 'teal', 'green', 'orange', 'cyan'];

// ─── Helpers ────────────────────────────────────────────────────────────
const initialsFrom = (name) => {
    if (!name) return '??';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '??';
};

const colorFor = (id) => {
    if (!id) return COLOR_PALETTE[0];
    const n = typeof id === 'number' ? id : String(id).split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    return COLOR_PALETTE[Math.abs(n) % COLOR_PALETTE.length];
};

const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return '';
    const m = Math.floor(diff / 60000);
    if (m < 1)  return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7)  return `${d}d`;
    return `${Math.floor(d / 7)}w`;
};

// Render content with #hashtag highlighting (XSS-safe — splits on regex,
// no innerHTML).
const renderContent = (text) => {
    if (!text) return null;
    const parts = text.split(/(#\w+)/g);
    return parts.map((part, i) => {
        if (part.startsWith('#')) {
            return (
                <span key={i} className="text-primary cursor-pointer hover:underline" style={{ fontWeight: 500 }}>
                    {part}
                </span>
            );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
};

// Detect document files by extension — fallback for posts where the
// backend doesn't tag media with type='document' explicitly.
const DOCUMENT_EXT_RE = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|csv|txt)(\?.*)?$/i;
const isDocumentMedia = (m) =>
    m && (m.type === 'document' || m.type === 'file' || DOCUMENT_EXT_RE.test(m.url || ''));

// Format bytes → human-readable (KB / MB).
const formatBytes = (bytes) => {
    if (!bytes || bytes < 0) return '';
    if (bytes < 1024)               return `${bytes} B`;
    if (bytes < 1024 * 1024)        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Map a file extension to a PrimeIcon + tint colour for the doc card.
const docIconMeta = (ext) => {
    const e = (ext || '').toLowerCase();
    if (e === 'pdf')                   return { icon: 'pi-file-pdf',   color: 'var(--red-500)'    };
    if (e === 'doc' || e === 'docx')   return { icon: 'pi-file-word',  color: 'var(--blue-500)'   };
    if (e === 'xls' || e === 'xlsx' || e === 'csv') return { icon: 'pi-file-excel', color: 'var(--green-500)' };
    if (e === 'ppt' || e === 'pptx')   return { icon: 'pi-file',       color: 'var(--orange-500)' };
    return                                    { icon: 'pi-file',       color: 'var(--surface-500)' };
};

// Normalise a post's media field — production may return either a flat
// array of URL strings, or [{ url, type }] objects, or both.
const normalizeMedia = (post) => {
    const raw = post?.media || post?.media_urls_full || post?.media_urls || [];
    if (!Array.isArray(raw)) return [];
    return raw
        .map((m) => {
            if (typeof m === 'string') {
                return { url: m, type: post.media_type || 'image' };
            }
            if (m && typeof m === 'object') {
                return { url: m.url, type: m.type || post.media_type || 'image' };
            }
            return null;
        })
        .filter((m) => m && m.url);
};

// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════
const SocialFeed = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);
    const mainColumnRef = useRef(null);

    // ── Notifications context ────────────────────────────────────────────
    const { markFeedViewed, fetchPendingConnectionsCount, fetchNewFeedPostsCount } = useNotifications();

    // Refresh notification counts ONLY when on this page.
    useEffect(() => {
        markFeedViewed();
        fetchPendingConnectionsCount();
        fetchNewFeedPostsCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Authenticated user / profile (for left-rail mini card) ──────────
    const [profile, setProfile] = useState(null);
    useEffect(() => {
        ProfileService.getMyProfile()
            .then((res) => res?.success && setProfile(res.data))
            .catch(() => { /* silent — profile card simply hides */ });
    }, []);

    // ── Tab + filters ────────────────────────────────────────────────────
    const [activeTab, setActiveTabRaw] = useState(0);
    const [filterType, setFilterType] = useState('');
    const [sortBy, setSortBy] = useState('recent');

    const setActiveTab = (idx) => {
        setActiveTabRaw(idx);
        setTimeout(() => {
            mainColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    };

    // ── Mobile detection (frosted ScrollTop is mobile-only) ─────────────
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 991px)');
        const apply = () => setIsMobile(mq.matches);
        apply();
        if (mq.addEventListener) mq.addEventListener('change', apply);
        else mq.addListener(apply);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', apply);
            else mq.removeListener(apply);
        };
    }, []);

    // ── Feed state ───────────────────────────────────────────────────────
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [expandedComments, setExpandedComments] = useState(new Set());
    const [networkBlocked, setNetworkBlocked] = useState(null);

    // ── Right-rail data ──────────────────────────────────────────────────
    const [trending, setTrending] = useState([]);
    const [pymk, setPymk] = useState([]);

    // ── Single-post deep-link via ?post=<id> ────────────────────────────
    const focusedPostId = useMemo(() => {
        const id = searchParams.get('post');
        return id ? Number(id) : null;
    }, [searchParams]);
    const [focusedPost, setFocusedPost] = useState(null);

    useEffect(() => {
        if (!focusedPostId) {
            setFocusedPost(null);
            return;
        }
        socialFeedService
            .getPost(focusedPostId)
            .then((res) => {
                if (res.success) setFocusedPost(res.data);
                else {
                    toast.current?.show({ severity: 'error', summary: 'Post not found', life: 3000 });
                    navigate('/app/leadflow/feed', { replace: true });
                }
            })
            .catch(() => {
                toast.current?.show({ severity: 'error', summary: 'Failed to load post', life: 3000 });
                navigate('/app/leadflow/feed', { replace: true });
            });
    }, [focusedPostId, navigate]);

    // ── Fetch active tab ────────────────────────────────────────────────
    const fetchTab = useCallback(async (tab, page = 1, append = false) => {
        if (focusedPostId) return;
        if (append) setLoadingMore(true);
        else { setLoading(true); setCurrentPage(1); }

        try {
            let response;
            if (tab === 0) {
                const filters = { sort_by: sortBy, per_page: 10, page };
                if (filterType) filters.post_type = filterType;
                response = await socialFeedService.getFeed(filters);
            } else if (tab === 1) {
                response = await socialFeedService.getMyPosts(10, page);
            } else {
                response = await socialFeedService.getSavedPosts(10, page);
            }

            if (response.success) {
                const responseData = response.data;
                const postsData = responseData?.data || responseData || [];
                const lastPage = responseData?.last_page;
                const curr = responseData?.current_page || page;
                setPosts((prev) =>
                    append ? [...prev, ...(Array.isArray(postsData) ? postsData : [])] : (Array.isArray(postsData) ? postsData : [])
                );
                setCurrentPage(curr);
                setHasMore(lastPage ? curr < lastPage : false);
            } else if (response.code?.startsWith('ln_')) {
                setNetworkBlocked({
                    code: response.code,
                    message: response.message,
                    reason: response.reason,
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to load feed',
                    life: 3000,
                });
            }
        } catch (error) {
            const errData = error?.response?.data;
            if (error?.response?.status === 403 && errData?.code?.startsWith('ln_')) {
                setNetworkBlocked({
                    code: errData.code,
                    message: errData.message,
                    reason: errData.reason,
                });
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Notice',
                    detail: 'Failed to load feed',
                    life: 3000,
                });
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [focusedPostId, sortBy, filterType]);

    // Reload when tab / sort / filter changes
    useEffect(() => {
        if (focusedPostId) return;
        fetchTab(activeTab, 1, false);
    }, [activeTab, fetchTab, focusedPostId]);

    // Fetch right-rail data once
    useEffect(() => {
        socialFeedService.getTrendingHashtags(10).then((res) => {
            if (res.success) setTrending(res.data?.data || res.data || []);
        }).catch(() => {});
        socialFeedService.getPeopleSuggestions(5).then((res) => {
            if (res.success) setPymk(res.data?.data || res.data || []);
        }).catch(() => {});
    }, []);

    // ── Composer state ───────────────────────────────────────────────────
    const [composerExpanded, setComposerExpanded] = useState(false);
    const [composerContent, setComposerContent] = useState('');
    const [composerVisibility, setComposerVisibility] = useState('public');
    const [composerType, setComposerType] = useState('general');
    /* Media items: [{ id, file, url, type, mime_type, size, uploading, error }, …]
       url is set after upload completes. While uploading, only id + file are set. */
    const [composerMedia, setComposerMedia] = useState([]);
    /* Marketplace lead attached to a lead-share post. Holds the full listing
       object (id + lead_snapshot/lead) so the dialog body can render a
       preview without a second fetch. Set by the LeadPicker, included in the
       submit payload as `linked_marketplace_lead_id`. */
    const [composerLinkedLead, setComposerLinkedLead] = useState(null);
    const [posting, setPosting] = useState(false);

    const handleComposerSubmit = async () => {
        const content = composerContent.trim();
        const hasContent = content.length > 0;
        const uploadedMedia = composerMedia.filter((m) => !m.uploading && !m.error && m.url);
        const hasMedia = uploadedMedia.length > 0;
        /* Allow text-only OR media-only posts (LinkedIn / Twitter convention).
           Reject if neither is present — backend would 422 anyway. */
        if (!hasContent && !hasMedia) return;
        /* Block submit while any upload is still in flight. */
        if (composerMedia.some((m) => m.uploading)) return;

        setPosting(true);
        try {
            /* Compute media_type: single bucket → that bucket, multiple buckets → "mixed".
               Mirrors the backend's media_type column constraint. */
            let mediaType;
            if (hasMedia) {
                const types = new Set(uploadedMedia.map((m) => m.type));
                mediaType = types.size === 1 ? [...types][0] : 'mixed';
            }
            const response = await socialFeedService.createPost({
                content,
                post_type: composerType,
                visibility: composerVisibility,
                hashtags: (content.match(/#\w+/g) || []),
                ...(hasMedia && {
                    media_type: mediaType,
                    /* Send array of { url, type, mime_type, size } objects — the
                       FeedController accepts both strings and full objects and
                       extracts the URL, but objects let us preserve metadata. */
                    media_urls: uploadedMedia.map((m) => ({
                        url:       m.url,
                        type:      m.type,
                        mime_type: m.mime_type,
                        size:      m.size,
                    })),
                }),
                /* When a marketplace lead is attached (via the Lead Picker),
                   send its id under the field the backend expects. The
                   FeedController validates this exists in central.marketplace_leads. */
                ...(composerLinkedLead?.id && {
                    linked_marketplace_lead_id: composerLinkedLead.id,
                }),
            });
            if (response.success) {
                toast.current?.show({ severity: 'success', summary: 'Posted', life: 1500 });
                markFeedViewed();
                setComposerContent('');
                setComposerExpanded(false);
                setComposerType('general');
                setComposerVisibility('public');
                setComposerMedia([]);
                setComposerLinkedLead(null);
                fetchTab(activeTab, 1, false);
            } else {
                throw new Error(response.message || 'Post failed');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Post failed',
                detail: error.response?.data?.message || error.message,
                life: 4000,
            });
        } finally {
            setPosting(false);
        }
    };

    // ── Per-post action handlers ────────────────────────────────────────
    const replacePost = (updated) => {
        setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
        if (focusedPost && focusedPost.id === updated.id) {
            setFocusedPost((prev) => ({ ...prev, ...updated }));
        }
    };

    const handleToggleLike = async (post) => {
        const willLike = !post.is_liked;
        // Optimistic
        replacePost({
            id: post.id,
            is_liked: willLike,
            like_count: Math.max(0, (post.like_count || 0) + (willLike ? 1 : -1)),
            user_reaction: willLike ? 'like' : null,
        });
        try {
            if (willLike) await socialFeedService.addReaction(post.id, 'like');
            else await socialFeedService.removeReaction(post.id, post.user_reaction || 'like');
        } catch (error) {
            // Roll back
            replacePost({
                id: post.id,
                is_liked: post.is_liked,
                like_count: post.like_count,
                user_reaction: post.user_reaction,
            });
            toast.current?.show({ severity: 'error', summary: 'Action failed', life: 2500 });
        }
    };

    const handleToggleSave = async (post) => {
        const willSave = !post.is_saved;
        replacePost({ id: post.id, is_saved: willSave });
        try {
            await socialFeedService.toggleSave(post.id, willSave);
            toast.current?.show({
                severity: 'success',
                summary: willSave ? 'Saved' : 'Removed from saved',
                life: 1500,
            });
            // On Saved tab, an unsave should pull the post out of the list
            if (activeTab === 2 && !willSave) {
                setPosts((prev) => prev.filter((p) => p.id !== post.id));
            }
        } catch (error) {
            replacePost({ id: post.id, is_saved: post.is_saved });
            toast.current?.show({ severity: 'error', summary: 'Action failed', life: 2500 });
        }
    };

    const handleToggleRepost = async (post) => {
        const willRepost = !post.is_reshared;
        replacePost({
            id: post.id,
            is_reshared: willRepost,
            share_count: Math.max(0, (post.share_count || 0) + (willRepost ? 1 : -1)),
        });
        try {
            if (willRepost) await socialFeedService.repostPost(post.id);
            else await socialFeedService.undoRepost(post.id);
        } catch (error) {
            replacePost({
                id: post.id,
                is_reshared: post.is_reshared,
                share_count: post.share_count,
            });
            toast.current?.show({ severity: 'error', summary: 'Action failed', life: 2500 });
        }
    };

    const handleDeletePost = (post) => {
        confirmDialog({
            message: 'Delete this post? This cannot be undone.',
            header: 'Delete post',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await socialFeedService.deletePost(post.id);
                    if (response.success) {
                        setPosts((prev) => prev.filter((p) => p.id !== post.id));
                        toast.current?.show({ severity: 'success', summary: 'Post deleted', life: 1500 });
                    } else {
                        throw new Error(response.message || 'Delete failed');
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Delete failed',
                        detail: error.response?.data?.message || error.message,
                        life: 4000,
                    });
                }
            },
        });
    };

    const handleSharePostLink = (post) => {
        const url = `${window.location.origin}/app/leadflow/feed?post=${post.id}`;
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                toast.current?.show({ severity: 'info', summary: 'Link copied', life: 1500 });
            });
        }
    };

    const toggleCommentsExpanded = (postId) => {
        setExpandedComments((prev) => {
            const next = new Set(prev);
            if (next.has(postId)) next.delete(postId);
            else next.add(postId);
            return next;
        });
    };

    // ── Counts for tab badges (best effort — comes from API totals) ─────
    const myPostsCount = useMemo(
        () => (profile?.stats?.posts_this_week ?? 0),
        [profile]
    );
    const savedCount = posts.filter((p) => p.is_saved).length;

    // ── LN gate Dialog ───────────────────────────────────────────────────
    const [lnDialogOpen, setLnDialogOpen] = useState(false);
    useEffect(() => { if (networkBlocked) setLnDialogOpen(true); }, [networkBlocked]);

    // ─── Render: visible posts (single-post view OR list) ──────────────
    const visiblePosts = focusedPost ? [focusedPost] : posts;

    return (
        <div className="grid lending-network-polish">
            <Toast ref={toast} position="top-right" />
            <ConfirmDialog />

            {/* Page header */}
            <div className="col-12">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">
                            {focusedPost ? 'Post' : 'Feed'}
                        </h1>
                        <p className="text-600 text-sm m-0">
                            {focusedPost ? 'Single-post view' : 'Posts from agents in your network'}
                        </p>
                    </div>
                    {focusedPost && (
                        <Button
                            label="Back to feed"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => {
                                setFocusedPost(null);
                                const next = new URLSearchParams(searchParams);
                                next.delete('post');
                                setSearchParams(next, { replace: true });
                            }}
                        />
                    )}
                </div>
            </div>

            {/* ─── LEFT RAIL (lg+, sticky) ─────────────────────────────── */}
            <div
                className="col-12 lg:col-3 hidden lg:block"
                style={{ alignSelf: 'flex-start', position: 'sticky', top: '1rem' }}
            >
                <MiniProfileCard profile={profile} navigate={navigate} />
                <SectionMenu
                    activeTab={activeTab}
                    onSelect={setActiveTab}
                    myPostsCount={myPostsCount}
                    savedCount={savedCount}
                />
            </div>

            {/* ─── MAIN COLUMN ─────────────────────────────────────────── */}
            <div className="col-12 lg:col-6" ref={mainColumnRef}>
                {!focusedPost && (
                    <ComposerCard
                        profile={profile}
                        expanded={composerExpanded}
                        setExpanded={setComposerExpanded}
                        content={composerContent}
                        setContent={setComposerContent}
                        visibility={composerVisibility}
                        setVisibility={setComposerVisibility}
                        type={composerType}
                        setType={setComposerType}
                        media={composerMedia}
                        setMedia={setComposerMedia}
                        linkedLead={composerLinkedLead}
                        setLinkedLead={setComposerLinkedLead}
                        posting={posting}
                        toastRef={toast}
                        onSubmit={handleComposerSubmit}
                    />
                )}

                {/* Section menu (mobile). Below lg the left rail is hidden, so the
                    SAME SectionMenu renders here instead of the bare TabView that
                    used to sit in this slot — that strip carried no icons and no
                    unread counts, so mobile users never saw the "My posts" /
                    "Saved" badges. Reusing the component keeps the two in sync. */}
                {!focusedPost && (
                    <div className="block lg:hidden">
                        <SectionMenu
                            activeTab={activeTab}
                            onSelect={setActiveTab}
                            myPostsCount={myPostsCount}
                            savedCount={savedCount}
                        />
                    </div>
                )}

                {/* Filter row — only meaningful on the Feed tab, and it's the sole
                    remaining occupant of this Card, so the Card itself is gated on
                    activeTab too. Otherwise tabs 1/2 rendered an empty box. */}
                {!focusedPost && activeTab === 0 && (
                    <Card
                        className="shadow-1 mb-3"
                        pt={{ body: { className: 'p-0' }, content: { className: 'p-0' } }}
                    >
                        {/* Two stock PrimeReact Dropdowns side-by-side — same
                            layout as the prototype (SocialFeedPrototype). Slim,
                            theme-default, no custom styling. */}
                        <div className="grid formgrid p-3 mt-0">
                            <div className="col-6">
                                <Dropdown
                                    value={filterType}
                                    options={TYPE_FILTER_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFilterType(e.value)}
                                    className="w-full p-inputtext-sm"
                                    placeholder="Type"
                                />
                            </div>
                            <div className="col-6">
                                <Dropdown
                                    value={sortBy}
                                    options={SORT_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setSortBy(e.value)}
                                    className="w-full p-inputtext-sm"
                                    placeholder="Sort"
                                />
                            </div>
                        </div>
                    </Card>
                )}

                {/* Posts stream — wrapped in lending-network-fade so each card
                    cascades in with a small staggered delay (50ms each). */}
                {loading && visiblePosts.length === 0 ? (
                    <SkeletonPosts />
                ) : visiblePosts.length === 0 ? (
                    <EmptyFeedState tab={activeTab} />
                ) : (
                    <div className="lending-network-fade">
                        {visiblePosts.map((post) => (
                            <PostCard
                                key={post.id}
                                post={post}
                                commentsExpanded={expandedComments.has(post.id)}
                                onToggleComments={() => toggleCommentsExpanded(post.id)}
                                onLike={() => handleToggleLike(post)}
                                onSave={() => handleToggleSave(post)}
                                onRepost={() => handleToggleRepost(post)}
                                onDelete={() => handleDeletePost(post)}
                                onShare={() => handleSharePostLink(post)}
                                toast={toast}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                )}

                {/* Load more */}
                {!focusedPost && hasMore && visiblePosts.length > 0 && (
                    <div className="text-center mt-3 mb-3">
                        <Button
                            label="Load more posts"
                            icon="pi pi-refresh"
                            severity="secondary"
                            outlined
                            size="small"
                            loading={loadingMore}
                            onClick={() => fetchTab(activeTab, currentPage + 1, true)}
                        />
                    </div>
                )}
            </div>

            {/* ─── RIGHT RAIL (drops to bottom on mobile) ──────────────── */}
            <div className="col-12 lg:col-3">
                <TrendingWidget hashtags={trending} navigate={navigate} />
                <PYMKWidget suggestions={pymk} navigate={navigate} />
                <QuickActionsWidget navigate={navigate} />
            </div>

            {/* Mobile-only frosted ScrollTop FAB (P25) */}
            {isMobile && (
                <>
                    <style>{`
                        .p-scrolltop {
                            bottom: 5rem !important;
                            background-color: rgba(120, 120, 128, 0.32) !important;
                            backdrop-filter: blur(12px) saturate(180%);
                            -webkit-backdrop-filter: blur(12px) saturate(180%);
                            border: 1px solid rgba(255, 255, 255, 0.4) !important;
                            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
                            width: 2.75rem !important;
                            height: 2.75rem !important;
                        }
                        .p-scrolltop .p-scrolltop-icon, .p-scrolltop .pi {
                            color: #1f2937 !important;
                            font-size: 1.1rem !important;
                            font-weight: 700 !important;
                        }
                    `}</style>
                    <ScrollTop threshold={300} icon="pi pi-arrow-up" behavior="smooth" />
                </>
            )}

            {/* LN gate Dialog */}
            <Dialog
                visible={lnDialogOpen && !!networkBlocked}
                onHide={() => setLnDialogOpen(false)}
                header={(
                    <div className="flex align-items-center gap-2">
                        <i
                            className={`pi ${networkBlocked?.code === 'ln_pending' ? 'pi-clock' : 'pi-lock'}`}
                            style={{
                                color: networkBlocked?.code === 'ln_pending' ? 'var(--orange-500)' : 'var(--red-500)',
                                fontSize: '1.2rem',
                            }}
                            aria-hidden="true"
                        />
                        <span className="text-900 font-semibold">
                            {networkBlocked?.code === 'ln_pending' ? 'Approval Pending' : 'Access Denied'}
                        </span>
                    </div>
                )}
                modal
                style={{ width: 'min(480px, 95vw)' }}
                breakpoints={{ '640px': '95vw' }}
                footer={<Button label="OK" size="small" onClick={() => setLnDialogOpen(false)} />}
            >
                <p className="text-700 text-sm line-height-3 m-0 mb-2">
                    {networkBlocked?.message || 'Your Lending Network access is being reviewed.'}
                </p>
                {networkBlocked?.reason && (
                    <p className="text-500 text-xs m-0">{networkBlocked.reason}</p>
                )}
            </Dialog>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════
const SkeletonPosts = () => (
    <>
        {[1, 2, 3].map((i) => (
            <Card key={i} className="shadow-1 mb-3" pt={{ body: { className: 'p-3' } }}>
                <div className="flex gap-2 mb-3">
                    <Skeleton shape="circle" size="2.75rem" />
                    <div className="flex-1">
                        <Skeleton width="40%" height="1rem" className="mb-2" />
                        <Skeleton width="60%" height="0.75rem" />
                    </div>
                </div>
                <Skeleton width="100%" height="3rem" className="mb-2" />
                <Skeleton width="80%" height="1rem" />
            </Card>
        ))}
    </>
);

// ─── Mini profile card (left rail) ──────────────────────────────────────
const MiniProfileCard = ({ profile, navigate }) => {
    if (!profile) {
        return (
            <Card className="shadow-1 mb-3" pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}>
                <Skeleton width="100%" height="6rem" />
            </Card>
        );
    }
    const color = colorFor(profile.id || profile.user_id);
    return (
        <Card
            className="shadow-1 mb-3 overflow-hidden"
            pt={{ body: { className: 'p-0' }, content: { className: 'p-0' } }}
        >
            <div style={{ height: '3rem', background: `linear-gradient(135deg, var(--${color}-400), var(--${color}-600))` }} />
            <div className="p-3 text-center" style={{ marginTop: '-1.75rem' }}>
                <Avatar
                    label={initialsFrom(profile.full_name)}
                    image={profile.profile_photo_url_full}
                    shape="circle"
                    style={{
                        width: '3.5rem',
                        height: '3.5rem',
                        backgroundColor: `var(--${color}-100)`,
                        color: `var(--${color}-700)`,
                        fontWeight: 700,
                        fontSize: '1rem',
                        border: '2px solid var(--surface-0)',
                        boxShadow: `0 0 0 2px var(--${color}-200)`,
                        marginBottom: '0.5rem',
                    }}
                />
                <div className="text-900 font-semibold text-sm">{profile.full_name}</div>
                {profile.headline && (
                    <div
                        className="text-600 text-xs line-height-3 mt-1"
                        style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {profile.headline}
                    </div>
                )}
                <Divider />
                <div className="grid text-left">
                    <div className="col-12 flex justify-content-between align-items-center text-xs mb-1">
                        <span className="text-600">Profile views</span>
                        <strong className="text-900">{profile.stats?.profile_views_week ?? 0}</strong>
                    </div>
                    <div className="col-12 flex justify-content-between align-items-center text-xs">
                        <span className="text-600">Posts this week</span>
                        <strong className="text-900">{profile.stats?.posts_this_week ?? 0}</strong>
                    </div>
                </div>
                <Button
                    label="View profile"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    text
                    size="small"
                    className="mt-2"
                    onClick={() => navigate('/app/my-agent-profile')}
                />
            </div>
        </Card>
    );
};

// ─── Section menu (left rail) ───────────────────────────────────────────
const SectionMenu = ({ activeTab, onSelect, myPostsCount, savedCount }) => {
    const items = [
        { label: 'Feed',     icon: 'pi-comments', index: 0 },
        { label: 'My posts', icon: 'pi-user',     index: 1, count: myPostsCount },
        { label: 'Saved',    icon: 'pi-bookmark', index: 2, count: savedCount },
    ];
    return (
        <Card className="shadow-1 mb-3" pt={{ body: { className: 'p-2' }, content: { className: 'p-0' } }}>
            <ul className="list-none p-0 m-0">
                {items.map((it) => {
                    const active = it.index === activeTab;
                    return (
                        <li
                            key={it.index}
                            onClick={() => onSelect(it.index)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(it.index)}
                            role="button"
                            tabIndex={0}
                            aria-current={active ? 'page' : undefined}
                            className={`flex align-items-center justify-content-between gap-2 p-2 border-round cursor-pointer transition-colors transition-duration-150 ${
                                active ? 'surface-100' : 'hover:surface-50'
                            }`}
                        >
                            <div className="flex align-items-center gap-2">
                                <i
                                    className={`pi ${it.icon} ${active ? 'text-primary' : 'text-500'}`}
                                    aria-hidden="true"
                                />
                                <span className={`text-sm ${active ? 'text-900 font-semibold' : 'text-700'}`}>
                                    {it.label}
                                </span>
                            </div>
                            {it.count > 0 && (
                                <Badge value={it.count} severity={active ? 'info' : null} />
                            )}
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
};

// ─── Composer (collapsed pill + expanded Dialog) ────────────────────────
/* Media-upload constraints — keep in sync with the backend's PostService::uploadMedia
   limits (max 5 files, 10 MB each, MIME whitelist). Catching at the UI level avoids
   doomed network calls + lets us show a friendly inline toast. */
const MAX_TOTAL_MEDIA  = 5;
const MAX_FILE_SIZE_MB = 10;
/* Allowed file types — kept in lockstep with the backend's
   FeedController::uploadMedia() $allowedMimes whitelist. Adding anything
   here that's NOT on that list would let the user select a file the
   server then rejects. Today's backend whitelist:
     image/jpeg, image/jpg, image/png, image/gif
     video/mp4, video/quicktime
     application/pdf
     application/vnd.openxmlformats-officedocument.spreadsheetml.sheet (.xlsx)
     application/vnd.ms-excel (.xls)
     text/plain (.txt)
   No webp, no docx, no csv, no ppt — the server will 400 on those. */
const ACCEPT_BY_TYPE = {
    image:    'image/jpeg,image/png,image/gif',
    video:    'video/mp4,video/quicktime',
    document: '.pdf,.xls,.xlsx,.txt',
};

const ComposerCard = ({
    profile, expanded, setExpanded, content, setContent,
    visibility, setVisibility, type, setType,
    media, setMedia, toastRef,
    linkedLead, setLinkedLead,
    posting, onSubmit,
}) => {
    /* Lead Picker open/close — local to this composer. Opening it will
       fetch marketplace listings on demand (no eager fetch). */
    const [leadPickerOpen, setLeadPickerOpen] = useState(false);

    /* Click handler shared by the collapsed-pill "Lead share" button and
       the toolbar inside the open dialog. Opens the picker first; on
       pick, the composer auto-expands with the lead pre-attached. */
    const openLeadPicker = () => {
        setType('lead_share');
        setLeadPickerOpen(true);
    };
    const handleLeadPicked = (listing) => {
        setLinkedLead(listing);
        setLeadPickerOpen(false);
        setExpanded(true);
    };
    const VISIBILITY_OPTIONS = Object.entries(VISIBILITY_META).map(([value, meta]) => ({
        label: meta.label, value, icon: meta.icon,
    }));
    const TYPE_OPTIONS = [
        { label: 'General post', value: 'general', icon: 'pi-comment' },
        ...Object.entries(POST_TYPE_META)
            .filter(([, meta]) => meta !== null)
            .map(([value, meta]) => ({ label: meta.label, value, icon: meta.icon })),
    ];

    const visibilityTemplate = (opt) => opt ? (
        <div className="flex align-items-center gap-2">
            <i className={`pi ${opt.icon}`} aria-hidden="true" />
            <span className="text-sm">{opt.label}</span>
        </div>
    ) : null;

    const color = colorFor(profile?.id || profile?.user_id);

    /* One hidden <input type="file"> per intent so each button gets its own
       accept attribute + the OS-native picker filters correctly. Triggered
       imperatively via ref.current.click(). */
    const photoInputRef    = useRef(null);
    const videoInputRef    = useRef(null);
    const documentInputRef = useRef(null);

    const inputRefByType = {
        image:    photoInputRef,
        video:    videoInputRef,
        document: documentInputRef,
    };

    const isAnyUploading = media.some((m) => m.uploading);

    /* Click a media-type button → expand the dialog (so the user sees previews
       immediately) → fire the OS file picker for that type. Kept in one helper
       so every entry point (collapsed pill row + expanded toolbar) uses the
       same logic. */
    const openFilePicker = (mediaType) => {
        setExpanded(true);
        const ref = inputRefByType[mediaType];
        if (ref?.current) {
            /* Clear previous selection so picking the same file twice still fires
               onChange (browsers suppress same-file events otherwise). */
            ref.current.value = '';
            ref.current.click();
        }
    };

    /* Validates each file against the size + count caps, kicks off the upload
       in parallel for everything that survives, and threads progress back into
       state via setMedia. Failures are surfaced inline (per-item .error) AND
       as a toast for user-visible feedback. */
    const handleFilesPicked = async (fileList, mediaType) => {
        if (!fileList || fileList.length === 0) return;
        const files = Array.from(fileList);

        /* Headroom check first — refuse the whole batch if it'd overflow the
           per-post cap. Friendlier than partial-add. */
        if (media.length + files.length > MAX_TOTAL_MEDIA) {
            toastRef?.current?.show({
                severity: 'warn',
                summary: 'Too many files',
                detail: `You can attach up to ${MAX_TOTAL_MEDIA} files per post.`,
                life: 3500,
            });
            return;
        }

        /* Per-file size validation. Keep good ones, reject bad ones with toast. */
        const validFiles = [];
        for (const f of files) {
            if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                toastRef?.current?.show({
                    severity: 'warn',
                    summary: 'File too large',
                    detail: `${f.name} is over ${MAX_FILE_SIZE_MB} MB and was skipped.`,
                    life: 3500,
                });
            } else {
                validFiles.push(f);
            }
        }
        if (validFiles.length === 0) return;

        /* Push optimistic placeholders so the user sees previews immediately,
           with object-URL thumbnails for images / videos and a filename chip
           for documents. id is a stable per-item key for React. */
        const placeholders = validFiles.map((f) => ({
            id:        `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file:      f,
            type:      mediaType,
            mime_type: f.type,
            size:      f.size,
            name:      f.name,
            preview:   mediaType === 'document' ? null : URL.createObjectURL(f),
            uploading: true,
            error:     null,
            url:       null,
        }));
        setMedia((prev) => [...prev, ...placeholders]);

        /* Single multipart upload covering the whole batch — one round-trip,
           lets the backend moderate + write to S3 in one go. The response data
           comes back ordered the same as files in the FormData. */
        try {
            const response = await socialFeedService.uploadMedia(validFiles);
            if (!response.success) {
                throw new Error(response.message || 'Upload failed');
            }
            const uploaded = Array.isArray(response.data) ? response.data : [];
            setMedia((prev) => prev.map((item) => {
                /* Match each placeholder to its uploaded counterpart by index
                   within this batch. Untouched placeholders stay as-is. */
                const idxInBatch = placeholders.findIndex((p) => p.id === item.id);
                if (idxInBatch === -1) return item;
                const u = uploaded[idxInBatch];
                if (!u) {
                    return { ...item, uploading: false, error: 'Upload returned no URL' };
                }
                return {
                    ...item,
                    uploading: false,
                    error:     null,
                    url:       u.url,
                    type:      u.type || item.type,
                    mime_type: u.mime_type || item.mime_type,
                    size:      u.size ?? item.size,
                };
            }));
        } catch (error) {
            const detail = error.response?.data?.message || error.message || 'Upload failed';
            toastRef?.current?.show({
                severity: 'error',
                summary: 'Upload failed',
                detail,
                life: 4000,
            });
            /* Mark the placeholders we created as failed so the user can retry
               or remove them. Other in-flight items are untouched. */
            const placeholderIds = new Set(placeholders.map((p) => p.id));
            setMedia((prev) => prev.map((item) =>
                placeholderIds.has(item.id)
                    ? { ...item, uploading: false, error: detail }
                    : item
            ));
        }
    };

    const removeMediaItem = (id) => {
        setMedia((prev) => {
            const removed = prev.find((m) => m.id === id);
            /* Free the object URL so we don't leak memory on long composer
               sessions (each pick creates a new blob URL). */
            if (removed?.preview) {
                try { URL.revokeObjectURL(removed.preview); } catch (_) { /* noop */ }
            }
            return prev.filter((m) => m.id !== id);
        });
    };

    const closeAndReset = () => {
        if (posting) return;
        /* Revoke any leftover object URLs from previews on cancel. */
        media.forEach((m) => {
            if (m.preview) {
                try { URL.revokeObjectURL(m.preview); } catch (_) { /* noop */ }
            }
        });
        setMedia([]);
        setLinkedLead(null);
        setExpanded(false);
    };

    /* Submit is enabled when there's text OR successfully-uploaded media,
       and nothing is currently uploading. Mirrors the parent's submit guard. */
    const hasContent       = content.trim().length > 0;
    const hasUploadedMedia = media.some((m) => !m.uploading && !m.error && m.url);
    const submitDisabled   = posting || isAnyUploading || (!hasContent && !hasUploadedMedia);

    return (
        <>
            {/* Hidden file inputs — one per intent, accept attr scoped to that
                media type. multiple on photos; single on video / document
                (industry standard — videos and docs are usually shared one at
                a time). */}
            <input
                ref={photoInputRef}
                type="file"
                accept={ACCEPT_BY_TYPE.image}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFilesPicked(e.target.files, 'image')}
            />
            <input
                ref={videoInputRef}
                type="file"
                accept={ACCEPT_BY_TYPE.video}
                style={{ display: 'none' }}
                onChange={(e) => handleFilesPicked(e.target.files, 'video')}
            />
            <input
                ref={documentInputRef}
                type="file"
                accept={ACCEPT_BY_TYPE.document}
                style={{ display: 'none' }}
                onChange={(e) => handleFilesPicked(e.target.files, 'document')}
            />

            <Card className="shadow-1 mb-3" pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}>
                <div className="flex align-items-center gap-3 mb-3">
                    <Avatar
                        label={initialsFrom(profile?.full_name)}
                        image={profile?.profile_photo_url_full}
                        shape="circle"
                        style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            border: '2px solid var(--surface-0)',
                            boxShadow: `0 0 0 2px var(--${color}-200)`,
                            flexShrink: 0,
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="flex-1 text-left bg-transparent cursor-pointer p-2 border-round surface-border border-1 text-600 text-sm transition-colors transition-duration-150 hover:surface-50"
                        style={{ outline: 'none' }}
                    >
                        Share a post, lead, or question…
                    </button>
                </div>
                {/* Each media-type button now actually does its job — opens the
                    matching file picker. Lead share + Article remain text-type
                    buttons (they don't take files; they set composerType). */}
                <div className="flex justify-content-around gap-1 flex-wrap">
                    <ComposerShortcutBtn icon="pi-image"     label="Photo"      iconColor="var(--green-500)"  onClick={() => openFilePicker('image')} />
                    <ComposerShortcutBtn icon="pi-video"     label="Video"      iconColor="var(--red-500)"    onClick={() => openFilePicker('video')} />
                    <ComposerShortcutBtn icon="pi-file"      label="Document"   iconColor="var(--orange-500)" onClick={() => openFilePicker('document')} />
                    <ComposerShortcutBtn icon="pi-share-alt" label="Lead share" iconColor="var(--blue-500)"   onClick={openLeadPicker} />
                    <ComposerShortcutBtn icon="pi-file-edit" label="Article"    iconColor="var(--purple-500)" onClick={() => { setType('article');    setExpanded(true); }} />
                </div>
            </Card>

            <Dialog
                visible={expanded}
                onHide={closeAndReset}
                modal
                draggable={false}
                resizable={false}
                style={{ width: 'min(560px, 95vw)' }}
                breakpoints={{ '768px': '100vw' }}
                pt={{
                    root: { style: { borderRadius: 'min(1rem, 0px)' } },
                    content: { className: 'p-0' },
                }}
                header={(
                    <div className="flex align-items-center gap-3">
                        <Avatar
                            label={initialsFrom(profile?.full_name)}
                            image={profile?.profile_photo_url_full}
                            shape="circle"
                            style={{
                                width: '2.5rem',
                                height: '2.5rem',
                                backgroundColor: `var(--${color}-100)`,
                                color: `var(--${color}-700)`,
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                border: '2px solid var(--surface-0)',
                                boxShadow: `0 0 0 2px var(--${color}-200)`,
                                flexShrink: 0,
                            }}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-900 font-semibold text-sm">{profile?.full_name || 'You'}</div>
                            <Dropdown
                                value={visibility}
                                options={VISIBILITY_OPTIONS}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setVisibility(e.value)}
                                valueTemplate={visibilityTemplate}
                                itemTemplate={visibilityTemplate}
                                className="p-inputtext-sm"
                                style={{ width: 'fit-content', maxWidth: '100%' }}
                            />
                        </div>
                    </div>
                )}
                footer={(
                    <div className="flex align-items-center justify-content-between gap-2 w-full">
                        <span className="text-500 text-xs">
                            {content.length}/3000{isAnyUploading ? ' · uploading…' : ''}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                label="Cancel"
                                severity="secondary"
                                text
                                size="small"
                                disabled={posting}
                                onClick={closeAndReset}
                            />
                            <Button
                                label="Post"
                                icon="pi pi-send"
                                size="small"
                                rounded
                                loading={posting}
                                disabled={submitDisabled}
                                onClick={onSubmit}
                            />
                        </div>
                    </div>
                )}
            >
                <div className="p-3">
                    <div className="overflow-x-auto mb-2" style={{ whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                        <div className="flex gap-1 align-items-center" style={{ minWidth: 'fit-content' }}>
                            {TYPE_OPTIONS.map((opt) => {
                                const active = type === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setType(opt.value)}
                                        className="cursor-pointer transition-colors transition-duration-150 flex align-items-center gap-1 flex-shrink-0"
                                        style={{
                                            padding: '0.35rem 0.75rem',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            borderRadius: '999px',
                                            border: active ? '1px solid var(--primary-color)' : '1px solid var(--surface-300)',
                                            backgroundColor: active ? 'var(--primary-color)' : 'var(--surface-0)',
                                            color: active ? 'var(--primary-color-text)' : 'var(--text-color)',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <i className={`pi ${opt.icon}`} style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <InputTextarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows={6}
                        autoResize
                        autoFocus
                        placeholder={
                            type === 'question'    ? 'Ask the network — what do you want to know?' :
                            type === 'lead_share'  ? 'Describe the lead — borrower, product, amount, location, what you need from the network…' :
                            type === 'article'     ? 'Write your article (title in the first line)…' :
                            type === 'achievement' ? 'Share what you accomplished…' :
                                                     'What do you want to share?'
                        }
                        className="w-full p-inputtext-sm mb-2"
                        maxLength={3000}
                    />

                    {/* Attached marketplace lead preview — shown when the user
                        has picked a lead via the Lead Picker. Lets them remove
                        it (×) and re-pick from the toolbar below. */}
                    {linkedLead && (
                        <div className="mb-3">
                            <ComposerLinkedLeadCard
                                listing={linkedLead}
                                onRemove={() => setLinkedLead(null)}
                            />
                        </div>
                    )}

                    {/* Media previews — render whenever something is attached.
                        Mixed types are fine; the grid auto-flows. Each tile
                        renders its own thumbnail / icon based on media.type. */}
                    {media.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {media.map((m) => (
                                <ComposerMediaTile
                                    key={m.id}
                                    item={m}
                                    onRemove={() => removeMediaItem(m.id)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Media toolbar — same affordances as the collapsed pill
                        row, available inside the open dialog so the user can
                        add attachments after they've started typing. */}
                    <div className="flex align-items-center gap-1 flex-wrap pt-2 border-top-1 surface-border">
                        <span className="text-500 text-xs mr-2">Attach:</span>
                        <Button
                            icon="pi pi-image"
                            label="Photo"
                            text
                            size="small"
                            severity="secondary"
                            disabled={media.length >= MAX_TOTAL_MEDIA || posting}
                            onClick={() => openFilePicker('image')}
                            pt={{ icon: { style: { color: 'var(--green-500)' } } }}
                        />
                        <Button
                            icon="pi pi-video"
                            label="Video"
                            text
                            size="small"
                            severity="secondary"
                            disabled={media.length >= MAX_TOTAL_MEDIA || posting}
                            onClick={() => openFilePicker('video')}
                            pt={{ icon: { style: { color: 'var(--red-500)' } } }}
                        />
                        <Button
                            icon="pi pi-file"
                            label="Document"
                            text
                            size="small"
                            severity="secondary"
                            disabled={media.length >= MAX_TOTAL_MEDIA || posting}
                            onClick={() => openFilePicker('document')}
                            pt={{ icon: { style: { color: 'var(--orange-500)' } } }}
                        />
                        <Button
                            icon="pi pi-share-alt"
                            label={linkedLead ? 'Change lead' : 'Lead'}
                            text
                            size="small"
                            severity="secondary"
                            disabled={posting}
                            onClick={openLeadPicker}
                            pt={{ icon: { style: { color: 'var(--blue-500)' } } }}
                        />
                        {media.length > 0 && (
                            <span className="text-400 text-xs ml-auto">
                                {media.length}/{MAX_TOTAL_MEDIA}
                            </span>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* Lead Picker — fetches user's marketplace listings on open
                and lets the user pick one. Independent of the main composer
                Dialog so we can stack them without z-index drama. */}
            <LeadPickerDialog
                visible={leadPickerOpen}
                onHide={() => setLeadPickerOpen(false)}
                onPick={handleLeadPicked}
                toastRef={toastRef}
            />
        </>
    );
};

/* ─── ComposerMediaTile — single attachment preview inside the composer ───
   Type-aware: images + videos get a thumbnail (using the local object URL
   while the upload is in flight, or the remote URL afterwards); documents
   get a labelled file chip with extension icon + size. The little × button
   in the corner removes the item from the composer. While the upload is in
   flight a translucent overlay + spinner is shown; if it failed we render
   an error chip in red so the user can remove + retry. */
const ComposerMediaTile = ({ item, onRemove }) => {
    const isImage = item.type === 'image';
    const isVideo = item.type === 'video';
    /* Prefer the local object URL while uploading (instant preview), fall
       back to the uploaded URL afterwards. */
    const src = item.preview || item.url;

    return (
        <div
            className="relative surface-100 border-round border-1 surface-border overflow-hidden"
            style={{
                width:  isImage || isVideo ? '6.5rem' : '12rem',
                height: '6.5rem',
                flexShrink: 0,
            }}
        >
            {(isImage || isVideo) && src ? (
                isVideo ? (
                    <video
                        src={src}
                        muted
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <img
                        src={src}
                        alt={item.name || 'attachment'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                )
            ) : (
                /* Document tile — extension-aware icon + filename + size */
                (() => {
                    const ext = (item.name || '').split('.').pop();
                    const meta = docIconMeta(ext);
                    return (
                        <div className="h-full flex flex-column justify-content-center px-2 py-1 gap-1">
                            <div className="flex align-items-center gap-2">
                                <i
                                    className={`pi ${meta.icon}`}
                                    style={{ fontSize: '1.2rem', color: meta.color }}
                                    aria-hidden="true"
                                />
                                <div
                                    className="text-900 text-xs font-semibold flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                    title={item.name}
                                >
                                    {item.name}
                                </div>
                            </div>
                            <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                {formatBytes(item.size)}
                            </div>
                        </div>
                    );
                })()
            )}

            {/* Loading overlay while uploading */}
            {item.uploading && (
                <div
                    className="absolute flex align-items-center justify-content-center"
                    style={{
                        inset: 0,
                        background: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(2px)',
                    }}
                >
                    <i
                        className="pi pi-spin pi-spinner text-primary"
                        style={{ fontSize: '1.4rem' }}
                        aria-hidden="true"
                    />
                </div>
            )}

            {/* Error overlay if upload failed */}
            {item.error && !item.uploading && (
                <div
                    className="absolute flex align-items-center justify-content-center text-center"
                    style={{
                        inset: 0,
                        background: 'rgba(239, 68, 68, 0.85)',
                        color: '#fff',
                        padding: '0.4rem',
                    }}
                    title={item.error}
                >
                    <div>
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '1rem' }} aria-hidden="true" />
                        <div style={{ fontSize: '0.6rem', fontWeight: 600 }}>Failed</div>
                    </div>
                </div>
            )}

            {/* Remove × — always visible, top-right, 28×28 hit area */}
            <button
                type="button"
                onClick={onRemove}
                aria-label={`Remove ${item.name || 'attachment'}`}
                className="absolute cursor-pointer flex align-items-center justify-content-center"
                style={{
                    top: '0.25rem',
                    right: '0.25rem',
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '999px',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: 'none',
                    fontSize: '0.7rem',
                }}
            >
                <i className="pi pi-times" aria-hidden="true" />
            </button>
        </div>
    );
};

/* ─── Lead-data accessors — shape-tolerant ──────────────────────────────
   Marketplace listings come back with the lead payload nested under one
   of three keys depending on the endpoint (lead_snapshot, lead, or the
   listing itself). Mirror MyListings.js's helpers so the composer reads
   the same fields the rest of the app does. */
const _leadData = (listing) =>
    listing?.lead_snapshot || listing?.lead || listing || {};

const _borrowerName = (listing) => {
    const l = _leadData(listing);
    if (l.borrower_name) return l.borrower_name;
    if (l.masked_name)   return l.masked_name;
    if (l.full_name)     return l.full_name;
    if (l.first_name || l.last_name) return `${l.first_name || ''} ${l.last_name || ''}`.trim();
    return 'Unknown borrower';
};

const _loanAmount = (listing) => {
    const l = _leadData(listing);
    return l.loan_amount_required || l.loan_amount || l.amount || 0;
};

const _productType = (listing) => {
    const l = _leadData(listing);
    /* product_type_detailed comes from Create Lead, normalised slug; nicer
       to display than the raw value, so titlecase the underscores. */
    const raw = l.product_type_detailed || l.product_type || '';
    if (!raw) return '';
    return String(raw).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const _formatINR = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/* ─── ComposerLinkedLeadCard — preview of attached marketplace lead ────
   Shown inside the composer body when the user has picked a lead via
   the Lead Picker. Compact card so it doesn't dominate the dialog —
   borrower + product + amount + city + a remove × in the corner. */
const ComposerLinkedLeadCard = ({ listing, onRemove }) => {
    const borrower = _borrowerName(listing);
    const product  = _productType(listing);
    const amount   = _loanAmount(listing);
    const city     = _leadData(listing).city || _leadData(listing).location_city;

    return (
        <div
            className="surface-100 border-round border-1 surface-border relative flex align-items-center gap-3"
            style={{ padding: '0.75rem 0.85rem' }}
        >
            <div
                className="flex align-items-center justify-content-center border-round flex-shrink-0"
                style={{
                    width:  '2.5rem',
                    height: '2.5rem',
                    background: 'var(--blue-50)',
                }}
            >
                <i
                    className="pi pi-share-alt"
                    style={{ fontSize: '1rem', color: 'var(--blue-700)' }}
                    aria-hidden="true"
                />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-500 text-xs font-medium mb-1">Marketplace lead attached</div>
                <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {borrower}
                </div>
                <div className="text-600 text-xs line-height-3 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {[product, amount > 0 ? _formatINR(amount) : null, city].filter(Boolean).join(' · ')}
                </div>
            </div>
            <button
                type="button"
                onClick={onRemove}
                aria-label="Remove attached lead"
                className="flex align-items-center justify-content-center cursor-pointer flex-shrink-0"
                style={{
                    width:  '1.75rem',
                    height: '1.75rem',
                    borderRadius: '999px',
                    background: 'rgba(0,0,0,0.06)',
                    border: 'none',
                    color: 'var(--text-color)',
                    fontSize: '0.75rem',
                }}
            >
                <i className="pi pi-times" aria-hidden="true" />
            </button>
        </div>
    );
};

/* ─── LeadPickerDialog — pick one of my marketplace listings ──────────
   Dialog opens → fetches my listings on demand (no eager fetch on
   composer mount). User can filter by borrower / product / city via a
   search bar; clicking a row picks the lead and closes the dialog.
   Stays simple — no pagination yet; per_page=50 covers the realistic
   range of "leads I've personally posted". */
const LeadPickerDialog = ({ visible, onHide, onPick, toastRef }) => {
    const [loading,  setLoading]  = useState(false);
    const [listings, setListings] = useState([]);
    const [error,    setError]    = useState(null);
    const [query,    setQuery]    = useState('');

    /* Fetch only when the dialog opens; reset state on close so the
       next open is fresh. */
    useEffect(() => {
        if (!visible) {
            setQuery('');
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await marketplaceService.getMyListings({ per_page: 50 });
                if (cancelled) return;
                if (response?.success) {
                    /* Laravel paginate returns { data: { data: [...] } }. Be
                       defensive — also handle flat arrays. */
                    const raw = response.data;
                    const arr = Array.isArray(raw)         ? raw
                              : Array.isArray(raw?.data)   ? raw.data
                              : Array.isArray(raw?.listings) ? raw.listings
                              : [];
                    setListings(arr);
                } else {
                    throw new Error(response?.message || 'Failed to load listings');
                }
            } catch (e) {
                if (cancelled) return;
                const msg = e.response?.data?.message || e.message || 'Failed to load listings';
                setError(msg);
                toastRef?.current?.show({
                    severity: 'error',
                    summary: 'Could not load your leads',
                    detail: msg,
                    life: 4000,
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [visible, toastRef]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return listings;
        return listings.filter((l) => {
            const borrower = _borrowerName(l).toLowerCase();
            const product  = _productType(l).toLowerCase();
            const city     = (_leadData(l).city || _leadData(l).location_city || '').toLowerCase();
            return borrower.includes(q) || product.includes(q) || city.includes(q);
        });
    }, [listings, query]);

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header="Share one of your marketplace leads"
            modal
            draggable={false}
            resizable={false}
            style={{ width: 'min(560px, 95vw)' }}
            breakpoints={{ '768px': '100vw' }}
            pt={{
                root:    { style: { borderRadius: 'min(1rem, 0px)' } },
                content: { className: 'p-3' },
            }}
        >
            <p className="text-600 text-sm m-0 mb-3">
                Pick a lead you&rsquo;ve already listed in the marketplace. The post will link
                back to it so people can apply.
            </p>

            <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by borrower, product, or city…"
                className="w-full p-inputtext p-component p-inputtext-sm mb-3"
                autoComplete="off"
                spellCheck={false}
                maxLength={100}
            />

            {loading ? (
                <div className="flex flex-column gap-2">
                    <Skeleton width="100%" height="4rem" borderRadius="0.5rem" />
                    <Skeleton width="100%" height="4rem" borderRadius="0.5rem" />
                    <Skeleton width="100%" height="4rem" borderRadius="0.5rem" />
                </div>
            ) : error ? (
                <div className="text-center py-5 text-500 text-sm">
                    <i className="pi pi-exclamation-circle text-orange-500 mb-2" style={{ fontSize: '1.5rem' }} aria-hidden="true" />
                    <div>{error}</div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-500 text-sm">
                    {listings.length === 0 ? (
                        <>
                            <i className="pi pi-inbox mb-2" style={{ fontSize: '1.5rem', color: 'var(--surface-500)' }} aria-hidden="true" />
                            <div className="text-700 font-medium mb-1">No marketplace leads yet</div>
                            <div>List a lead in the marketplace first, then come back to share it here.</div>
                        </>
                    ) : (
                        <>No leads match &ldquo;{query.trim()}&rdquo;.</>
                    )}
                </div>
            ) : (
                <ul
                    className="list-none p-0 m-0"
                    style={{ maxHeight: '50vh', overflowY: 'auto' }}
                >
                    {filtered.map((listing) => (
                        <LeadPickerRow
                            key={listing.id}
                            listing={listing}
                            onPick={() => onPick(listing)}
                        />
                    ))}
                </ul>
            )}
        </Dialog>
    );
};

/* ─── LeadPickerRow — single tappable row inside the picker ──────────── */
const LeadPickerRow = ({ listing, onPick }) => {
    const borrower = _borrowerName(listing);
    const product  = _productType(listing);
    const amount   = _loanAmount(listing);
    const city     = _leadData(listing).city || _leadData(listing).location_city;
    const status   = listing.status;

    return (
        <li className="border-bottom-1 surface-border">
            <button
                type="button"
                onClick={onPick}
                className="w-full flex align-items-center gap-3 px-2 py-3 cursor-pointer hover:surface-50 transition-colors transition-duration-150 text-left"
                style={{ background: 'transparent', border: 0 }}
            >
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width:  '2.5rem',
                        height: '2.5rem',
                        background: 'var(--blue-50)',
                    }}
                >
                    <i
                        className="pi pi-briefcase"
                        style={{ fontSize: '1rem', color: 'var(--blue-700)' }}
                        aria-hidden="true"
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {borrower}
                    </div>
                    <div className="text-600 text-xs line-height-3 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {[product, amount > 0 ? _formatINR(amount) : null, city].filter(Boolean).join(' · ')}
                    </div>
                </div>
                {status && (
                    <Tag
                        value={status}
                        severity={status === 'active' ? 'success' : status === 'expired' ? 'warning' : 'info'}
                        style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                    />
                )}
            </button>
        </li>
    );
};

const ComposerShortcutBtn = ({ icon, label, iconColor, onClick }) => (
    <Button
        icon={`pi ${icon}`}
        label={label}
        text
        size="small"
        severity="secondary"
        onClick={onClick}
        pt={{
            label: { className: 'hidden md:inline' },
            icon: iconColor ? { style: { color: iconColor } } : undefined,
            root: { className: 'flex-1', style: { padding: '0.5rem 0.75rem' } },
        }}
    />
);

// ─── Post card ──────────────────────────────────────────────────────────
const PostCard = ({
    post, commentsExpanded, onToggleComments,
    onLike, onSave, onRepost, onDelete, onShare, toast, navigate,
}) => {
    const author = post.author_profile || {};
    const visMeta = VISIBILITY_META[post.visibility] || VISIBILITY_META.public;
    const typeMeta = POST_TYPE_META[post.post_type];
    const roleBadge = USER_TYPE_BADGE[author.user_type];
    const menuRef = useRef(null);

    /* Show more / Show less for long post bodies. Default collapsed when
       content exceeds POST_PREVIEW_CHARS — gives the feed a uniform skim
       rhythm and lets users opt into long content explicitly. */
    const [contentExpanded, setContentExpanded] = useState(false);
    const fullContent = post.content || '';
    const isLong = fullContent.length > POST_PREVIEW_CHARS;
    const visibleContent = (isLong && !contentExpanded)
        ? fullContent.slice(0, POST_PREVIEW_CHARS).trimEnd() + '…'
        : fullContent;

    const currentUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    }, []);
    const isMine =
        (post.user_id && currentUser.id && String(post.user_id) === String(currentUser.id))
        || (author.user_id && currentUser.id && String(author.user_id) === String(currentUser.id));

    const color = colorFor(author.id || author.user_id);
    const media = useMemo(() => normalizeMedia(post), [post]);

    const menuItems = [
        {
            label: post.is_saved ? 'Unsave' : 'Save',
            icon: post.is_saved ? 'pi pi-bookmark-fill' : 'pi pi-bookmark',
            command: onSave,
        },
        { label: 'Copy link', icon: 'pi pi-copy', command: onShare },
        ...(isMine ? [
            { separator: true },
            { label: 'Delete', icon: 'pi pi-trash', className: 'text-red-600', command: onDelete },
        ] : [
            { separator: true },
            { label: 'Not interested', icon: 'pi pi-thumbs-down' },
            { label: 'Report',         icon: 'pi pi-flag', className: 'text-red-600' },
        ]),
    ];

    return (
        <Card
            className="shadow-1 mb-3 overflow-hidden"
            pt={{ body: { className: 'p-0' }, content: { className: 'p-0' } }}
        >
            {/* Header */}
            <div className="flex align-items-start justify-content-between gap-2 p-3 pb-2">
                <div className="flex gap-2 flex-1 min-w-0">
                    <Avatar
                        label={initialsFrom(author.full_name)}
                        image={author.profile_photo_url_full}
                        shape="circle"
                        onClick={() => author.id && navigate(`/app/profile/${author.id}`)}
                        style={{
                            width: '2.75rem',
                            height: '2.75rem',
                            backgroundColor: `var(--${color}-100)`,
                            color: `var(--${color}-700)`,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            border: '2px solid var(--surface-0)',
                            boxShadow: `0 0 0 2px var(--${color}-200)`,
                            flexShrink: 0,
                            cursor: author.id ? 'pointer' : 'default',
                        }}
                    />
                    <div className="min-w-0">
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <span
                                className="text-900 font-semibold text-sm cursor-pointer hover:underline"
                                onClick={() => author.id && navigate(`/app/profile/${author.id}`)}
                            >
                                {author.full_name || 'Unknown'}
                            </span>
                            {/* Author role badge — translucent severity Tag,
                                rendered only when backend gives author.user_type. */}
                            {roleBadge && (
                                <Tag
                                    value={roleBadge.label}
                                    severity={roleBadge.severity}
                                    style={{ fontSize: '0.6rem', padding: '0.1rem 0.5rem' }}
                                />
                            )}
                            {typeMeta && (
                                <Tag
                                    value={typeMeta.label}
                                    icon={`pi ${typeMeta.icon}`}
                                    severity={typeMeta.severity}
                                    style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }}
                                />
                            )}
                        </div>
                        {author.headline && (
                            <div className="text-600 text-xs line-height-3 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {author.headline}
                            </div>
                        )}
                        <div className="text-400 text-xs mt-1 flex align-items-center gap-1">
                            <span>{timeAgo(post.created_at)}</span>
                            <span>·</span>
                            <i
                                className={`pi ${visMeta.icon}`}
                                style={{ fontSize: '0.65rem' }}
                                aria-hidden="true"
                                title={visMeta.label}
                            />
                        </div>
                    </div>
                </div>
                <Menu model={menuItems} popup ref={menuRef} />
                <Button
                    icon="pi pi-ellipsis-h"
                    text
                    rounded
                    size="small"
                    onClick={(e) => menuRef.current.toggle(e)}
                    aria-label="More actions"
                />
            </div>

            {/* Content — collapses behind a Show more button when long. */}
            <div className="px-3 pb-2">
                <p className="text-700 text-sm line-height-3 m-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderContent(visibleContent)}
                </p>
                {isLong && (
                    <Button
                        label={contentExpanded ? 'Show less' : 'Show more'}
                        icon={contentExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                        iconPos="right"
                        text
                        size="small"
                        severity="secondary"
                        onClick={() => setContentExpanded((v) => !v)}
                        pt={{
                            root: { style: { padding: '0.2rem 0.4rem', fontSize: '0.75rem', marginLeft: '-0.4rem' } },
                            label: { style: { fontWeight: 600 } },
                        }}
                    />
                )}
            </div>

            {/* Media */}
            {media.length > 0 && <MediaRenderer media={media} />}

            {/* Type-specific embeds — render only if backend includes the
                metadata (article / achievement / lead_card may be absent). */}
            {post.post_type === 'article' && post.article && (
                <ArticleEmbed article={post.article} />
            )}
            {post.post_type === 'achievement' && post.achievement && (
                <AchievementBadge achievement={post.achievement} />
            )}
            {post.post_type === 'lead_share' && (post.lead_card || post.lead_metadata) && (
                <LeadShareEmbed leadCard={post.lead_card || post.lead_metadata} />
            )}

            {/* Engagement summary — heart shown as a red-tinted circular pill
                so the reaction visually anchors the count (LinkedIn-style). */}
            {(post.like_count > 0 || post.comment_count > 0 || post.share_count > 0) && (
                <div className="flex align-items-center justify-content-between px-3 py-2 text-xs text-500">
                    <div className="flex align-items-center gap-2">
                        {post.like_count > 0 && (
                            <>
                                <span
                                    className="flex align-items-center justify-content-center border-circle"
                                    style={{
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        backgroundColor: 'var(--red-50)',
                                        color: 'var(--red-600)',
                                    }}
                                    aria-hidden="true"
                                >
                                    <i className="pi pi-heart-fill" style={{ fontSize: '0.65rem' }} />
                                </span>
                                <span>{post.like_count}</span>
                            </>
                        )}
                    </div>
                    <div className="flex gap-3">
                        {post.comment_count > 0 && (
                            <button
                                type="button"
                                className="bg-transparent border-none cursor-pointer text-500 text-xs p-0 hover:underline"
                                onClick={onToggleComments}
                            >
                                {post.comment_count} comment{post.comment_count > 1 ? 's' : ''}
                            </button>
                        )}
                        {post.share_count > 0 && (
                            <span>{post.share_count} share{post.share_count > 1 ? 's' : ''}</span>
                        )}
                    </div>
                </div>
            )}

            <Divider className="my-0" />

            {/* Action bar */}
            <div className="flex justify-content-around p-1 gap-1">
                <ActionBtn
                    icon={post.is_liked ? 'pi-heart-fill' : 'pi-heart'}
                    label={post.post_type === 'question' ? 'Useful' : 'Like'}
                    active={post.is_liked}
                    activeColor="var(--red-500)"
                    onClick={onLike}
                />
                <ActionBtn
                    icon="pi-comment"
                    label={post.post_type === 'question' ? 'Answer' : 'Comment'}
                    onClick={onToggleComments}
                />
                <ActionBtn
                    icon={post.is_reshared ? 'pi-replay' : 'pi-share-alt'}
                    label={post.is_reshared ? 'Reposted' : 'Repost'}
                    active={post.is_reshared}
                    activeColor="var(--green-600)"
                    onClick={onRepost}
                />
                <ActionBtn
                    icon={post.is_saved ? 'pi-bookmark-fill' : 'pi-bookmark'}
                    label="Save"
                    active={post.is_saved}
                    activeColor="var(--primary-color)"
                    onClick={onSave}
                />
            </div>

            {commentsExpanded && (
                <>
                    <Divider className="my-0" />
                    <CommentThread post={post} toast={toast} />
                </>
            )}
        </Card>
    );
};

const ActionBtn = ({ icon, label, active, activeColor, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="bg-transparent border-none cursor-pointer flex align-items-center justify-content-center gap-1 border-round transition-colors transition-duration-150 hover:surface-100"
        style={{
            flex: 1,
            padding: '0.5rem 0.25rem',
            fontWeight: 500,
            fontSize: '0.8rem',
            color: active ? activeColor : 'var(--text-color-secondary)',
            minWidth: 0,
        }}
        aria-pressed={active}
    >
        <i className={`pi ${icon}`} style={{ fontSize: '1rem' }} aria-hidden="true" />
        <span className="hidden sm:inline white-space-nowrap">{label}</span>
    </button>
);

// ─── Media renderer ─────────────────────────────────────────────────────
/* ─── Document attachment card ─────────────────────────────────────────
   Compact, fixed-height (~4.5rem) PDF / DOC / XLS / PPT attachment.
   Stays bounded so a posted file doesn't consume the whole post card.
   Tap "View" to open in a new tab — no inline preview iframe (would
   blow the size budget). Filename is single-line ellipsis. */
const DocumentAttachment = ({ doc }) => {
    const rawName = doc.name || doc.filename || decodeURIComponent(
        ((doc.url || '').split('/').pop() || '').split('?')[0] || 'Document'
    );
    const ext = (rawName.split('.').pop() || '').toLowerCase();
    const meta = docIconMeta(ext);
    const sizeLabel = doc.size ? formatBytes(doc.size) : null;

    return (
        <div
            className="flex align-items-center gap-3 p-2 surface-50 border-1 surface-border border-round-xl"
            style={{ minHeight: '4.5rem' }}
        >
            <div
                className="flex align-items-center justify-content-center border-round flex-shrink-0"
                style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--surface-0)',
                    border: '1px solid var(--surface-border)',
                }}
                aria-hidden="true"
            >
                <i className={`pi ${meta.icon}`} style={{ fontSize: '1.4rem', color: meta.color }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                    {rawName}
                </div>
                <div className="text-500 text-xs">
                    {ext ? ext.toUpperCase() : 'File'}
                    {sizeLabel ? ` · ${sizeLabel}` : ''}
                </div>
            </div>
            <Button
                label="View"
                icon="pi pi-external-link"
                outlined
                size="small"
                onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                pt={{
                    label: { className: 'hidden md:inline ml-1' },
                    root: {
                        className: 'flex-shrink-0',
                        style: { padding: '0.4rem 0.75rem', fontSize: '0.75rem' },
                    },
                }}
            />
        </div>
    );
};

const MediaRenderer = ({ media }) => {
    const [galleriaIndex, setGalleriaIndex] = useState(0);
    const [fullscreenVisible, setFullscreenVisible] = useState(false);

    if (!media || media.length === 0) return null;

    /* Split documents off from visual media so PDFs / docs render as
       compact attachment cards (fixed height, file-icon + name + size +
       View button) instead of falling through to <img> in the Galleria. */
    const documents = media.filter(isDocumentMedia);
    const visual    = media.filter((m) => !isDocumentMedia(m));

    /* If the post has ONLY documents, render the doc list and stop. */
    if (visual.length === 0 && documents.length > 0) {
        return (
            <div className="px-3 mb-3 flex flex-column gap-2">
                {documents.map((d, i) => <DocumentAttachment key={i} doc={d} />)}
            </div>
        );
    }

    /* Mixed: render docs first, then fall through to images/videos with
       the visual subset. */
    const docsBlock = documents.length > 0 ? (
        <div className="px-3 mb-3 flex flex-column gap-2">
            {documents.map((d, i) => <DocumentAttachment key={i} doc={d} />)}
        </div>
    ) : null;

    if (visual.length === 1 && visual[0].type === 'image') {
        return (
            <>
                {docsBlock}
                <div className="px-3 mb-3 flex justify-content-center">
                    <Image
                        src={visual[0].url}
                        alt="Post media"
                        preview
                        pt={{
                            image: {
                                className: 'border-round-xl',
                                style: { maxHeight: '450px', width: '100%', objectFit: 'cover' },
                            },
                        }}
                        style={{ width: '100%' }}
                    />
                </div>
            </>
        );
    }
    if (visual.length === 1 && visual[0].type === 'video') {
        return (
            <>
                {docsBlock}
                <div className="px-3 mb-3">
                    <div
                        className="border-round-xl overflow-hidden"
                        style={{ paddingBottom: '56.25%', position: 'relative', backgroundColor: 'var(--surface-100)' }}
                    >
                        <iframe
                            title="Post video"
                            src={visual[0].url}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                            allowFullScreen
                        />
                    </div>
                </div>
            </>
        );
    }
    const inlineItemTpl = (item) => (
        <div className="cursor-pointer" onClick={() => setFullscreenVisible(true)} style={{ width: '100%', height: '100%' }}>
            <img src={item.url} alt="" style={{ width: '100%', height: '320px', objectFit: 'cover', display: 'block' }} />
        </div>
    );
    const fullscreenItemTpl = (item) => (
        <img src={item.url} alt="" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
    );
    return (
        <>
            {docsBlock}
            <div className="px-3 mb-3">
                <div className="border-round-xl overflow-hidden surface-100">
                    <Galleria
                        value={visual}
                        activeIndex={galleriaIndex}
                        onItemChange={(e) => setGalleriaIndex(e.index)}
                        numVisible={1}
                        showItemNavigators
                        showThumbnails={false}
                        showIndicators
                        showIndicatorsOnItem
                        circular
                        item={inlineItemTpl}
                        responsiveOptions={[{ breakpoint: '768px', numVisible: 1 }]}
                        pt={{
                            content: { className: 'border-round-xl' },
                            indicators: { className: 'p-2' },
                        }}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
            <Galleria
                value={visual}
                visible={fullscreenVisible}
                onHide={() => setFullscreenVisible(false)}
                activeIndex={galleriaIndex}
                onItemChange={(e) => setGalleriaIndex(e.index)}
                fullScreen
                showThumbnails={false}
                showItemNavigators
                circular
                item={fullscreenItemTpl}
            />
        </>
    );
};


// ─── Type-specific embeds ───────────────────────────────────────────────
const ArticleEmbed = ({ article }) => (
    <div className="mx-3 mb-3 surface-border border-1 border-round-xl overflow-hidden cursor-pointer hover:shadow-2 transition-shadow transition-duration-200">
        <div style={{ height: '6rem', background: 'linear-gradient(135deg, var(--blue-400), var(--blue-600))' }} />
        <div className="p-3">
            <div className="flex align-items-center gap-2 text-500 text-xs mb-1">
                <i className="pi pi-file" aria-hidden="true" />
                <span>Article{article.read_time ? ` · ${article.read_time}` : ''}</span>
            </div>
            <div className="text-900 font-semibold text-sm mb-1">{article.title}</div>
            {article.excerpt && (
                <div className="text-600 text-xs line-height-3">{article.excerpt}</div>
            )}
            <Button
                label="Read article"
                icon="pi pi-arrow-right"
                iconPos="right"
                text
                size="small"
                className="mt-2 p-0"
            />
        </div>
    </div>
);

const AchievementBadge = ({ achievement }) => {
    const accent = achievement.color || 'amber';
    return (
        <div
            className="mx-3 mb-3 border-round-xl p-3 flex align-items-center gap-3"
            style={{
                background: `linear-gradient(135deg, var(--${accent}-100), var(--${accent}-50))`,
                border: `1px solid var(--${accent}-200)`,
            }}
        >
            <i
                className={`pi ${achievement.icon || 'pi-star-fill'}`}
                style={{ color: `var(--${accent}-600)`, fontSize: '1.75rem' }}
                aria-hidden="true"
            />
            <div>
                <div className="text-900 font-bold">{achievement.badge || achievement.title || 'Milestone'}</div>
                <div className="text-700 text-xs">Milestone unlocked</div>
            </div>
        </div>
    );
};

/* ─── LeadShareEmbed — clickable mini-card linking to the marketplace
   listing detail page. Backend serves the lead_card payload with
   already-masked borrower / amount fields, so this component just
   formats the layout. The whole card is the click target — leveraging
   `useNavigate()` to push to /app/marketplace/listing/:id where the
   full record is gated by the existing authorisation rules. */
const LeadShareEmbed = ({ leadCard }) => {
    const navigate = useNavigate();
    const handleOpen = () => {
        if (leadCard?.id) {
            navigate(`/app/marketplace/listing/${leadCard.id}`);
        }
    };
    const handleKey = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
        }
    };
    /* Build the meta line from whichever masked fields the backend gave us;
       skip blanks so we never render "·" with nothing on either side. */
    const metaLine = [leadCard.product, leadCard.amount_display, leadCard.city]
        .filter(Boolean)
        .join(' · ');

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={handleKey}
            aria-label={`Open marketplace lead from ${leadCard.borrower || 'shared post'}`}
            className="mx-3 mb-3 surface-border border-1 border-round-xl p-3 cursor-pointer transition-all transition-duration-150 hover:shadow-2"
            style={{
                backgroundColor: 'var(--surface-50)',
                borderLeft: '3px solid var(--primary-500)',
            }}
        >
            <div className="flex align-items-center gap-2 text-xs text-500 mb-2">
                <i className="pi pi-share-alt" aria-hidden="true" />
                <span className="uppercase font-semibold tracking-wider">Lead share</span>
            </div>
            <div className="flex align-items-center gap-3">
                <div className="flex-1 min-w-0">
                    <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {leadCard.borrower || 'Confidential'}
                    </div>
                    {metaLine && (
                        <div className="text-600 text-xs line-height-3 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {metaLine}
                        </div>
                    )}
                </div>
                {/* Visual affordance — small chevron in a primary tinted
                    circle to show the card is tappable. */}
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width:  '2rem',
                        height: '2rem',
                        background: 'var(--primary-50)',
                    }}
                >
                    <i
                        className="pi pi-arrow-right text-primary"
                        style={{ fontSize: '0.85rem' }}
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>
    );
};

// ─── Comment thread ─────────────────────────────────────────────────────
const QUICK_EMOJIS = [
    { emoji: '👏', text: 'Congratulations' },
    { emoji: '💯', text: 'Great work' },
    { emoji: '🎉', text: 'Amazing' },
    { emoji: '👍', text: 'Well done' },
    { emoji: '🔥', text: 'Impressive' },
    { emoji: '💡', text: 'Insightful' },
];

const INITIAL_COMMENTS_VISIBLE = 2;

const CommentThread = ({ post, toast }) => {
    const [comments, setComments] = useState([]);
    const [reply, setReply] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        socialFeedService
            .getComments(post.id)
            .then((res) => {
                if (cancelled) return;
                if (res.success) setComments(res.data?.data || res.data || []);
            })
            .catch(() => {})
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [post.id]);

    // Newest-first
    const sorted = useMemo(() => [...comments].reverse(), [comments]);
    const visibleComments = showAll ? sorted : sorted.slice(0, INITIAL_COMMENTS_VISIBLE);
    const hidden = comments.length - INITIAL_COMMENTS_VISIBLE;

    const handleSubmit = async () => {
        const body = reply.trim();
        if (!body) return;
        setSubmitting(true);
        try {
            const response = await socialFeedService.addComment(post.id, body);
            if (response.success) {
                const newComment = response.data?.data || response.data;
                setComments((prev) => [...prev, newComment]);
                setReply('');
                setShowAll(true);
                toast.current?.show({ severity: 'success', summary: 'Comment posted', life: 1200 });
            } else {
                throw new Error(response.message || 'Failed');
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Comment failed', life: 2500 });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-3 surface-50">
            <div className="overflow-x-auto mb-3" style={{ whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                <div className="flex gap-1 align-items-center" style={{ minWidth: 'fit-content' }}>
                    {QUICK_EMOJIS.map((q) => (
                        <button
                            key={q.text}
                            type="button"
                            onClick={() => setReply((reply ? reply + ' ' : '') + q.emoji + ' ' + q.text)}
                            className="bg-transparent surface-border border-1 cursor-pointer transition-colors transition-duration-150 hover:surface-100 flex-shrink-0"
                            style={{ padding: '0.3rem 0.7rem', borderRadius: '999px', fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                        >
                            {q.emoji} {q.text}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex gap-2 mb-3 align-items-end">
                <InputTextarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={1}
                    autoResize
                    placeholder="Write a comment…"
                    className="w-full p-inputtext-sm flex-1"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
                    }}
                    disabled={submitting}
                />
                <Button
                    icon="pi pi-send"
                    size="small"
                    rounded
                    loading={submitting}
                    disabled={!reply.trim() || submitting}
                    onClick={handleSubmit}
                    aria-label="Post comment"
                />
            </div>

            {!showAll && hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="bg-transparent border-none cursor-pointer text-primary text-xs font-medium hover:underline mb-2 p-0"
                >
                    View {hidden} previous comment{hidden > 1 ? 's' : ''}
                </button>
            )}

            {loading ? (
                <div className="text-500 text-sm text-center py-2">Loading comments…</div>
            ) : comments.length === 0 ? (
                <div className="text-500 text-sm text-center py-2">Be the first to comment.</div>
            ) : (
                <ul className="list-none p-0 m-0">
                    {visibleComments.map((c) => {
                        const a = c.author_profile || {};
                        const cColor = colorFor(a.id || a.user_id);
                        return (
                            <li key={c.id} className="flex gap-2 py-2">
                                <Avatar
                                    label={initialsFrom(a.full_name)}
                                    image={a.profile_photo_url_full}
                                    shape="circle"
                                    style={{
                                        width: '2rem', height: '2rem',
                                        backgroundColor: `var(--${cColor}-100)`,
                                        color: `var(--${cColor}-700)`,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        flexShrink: 0,
                                    }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="surface-0 border-round p-2">
                                        <div className="text-900 font-semibold text-xs">{a.full_name || 'Anonymous'}</div>
                                        <div
                                            className="text-700 text-sm line-height-3"
                                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {c.content}
                                        </div>
                                    </div>
                                    <div className="text-500 text-xs mt-1 flex gap-2">
                                        <span>{timeAgo(c.created_at)}</span>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {showAll && hidden > 0 && (
                <button
                    type="button"
                    onClick={() => setShowAll(false)}
                    className="bg-transparent border-none cursor-pointer text-primary text-xs font-medium hover:underline mt-2 p-0"
                >
                    Show fewer comments
                </button>
            )}
        </div>
    );
};

// ─── Right-rail widgets ─────────────────────────────────────────────────
const TrendingWidget = ({ hashtags = [] }) => {
    if (!hashtags.length) return null;
    return (
        <SideWidget icon="pi-hashtag" title="Trending in your network">
            <ul className="list-none p-0 m-0">
                {hashtags.slice(0, 5).map((t, idx) => {
                    const tag = t.tag || t.hashtag || t.name || (typeof t === 'string' ? t : '');
                    const count = t.posts_24h ?? t.count ?? t.posts_count ?? 0;
                    return (
                        <li
                            key={tag || idx}
                            className="flex align-items-center justify-content-between py-2 cursor-pointer hover:surface-50 border-round"
                        >
                            <div className="flex align-items-center gap-2 min-w-0">
                                <span className="text-400 text-xs font-bold" style={{ width: '1rem' }}>
                                    #{idx + 1}
                                </span>
                                <div className="min-w-0">
                                    <div className="text-900 text-sm font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        #{tag}
                                    </div>
                                    {count > 0 && (
                                        <div className="text-500 text-xs">{count} posts in 24h</div>
                                    )}
                                </div>
                            </div>
                            <i className="pi pi-chart-line text-400" aria-hidden="true" />
                        </li>
                    );
                })}
            </ul>
        </SideWidget>
    );
};

const PYMKWidget = ({ suggestions = [], navigate }) => {
    if (!suggestions.length) return null;

    /* Defensive: the suggestions API can return the photo URL under several
       different field names depending on whether it's the profile object,
       the user object, or the joined view. Try them in order. */
    const photoUrl = (s) =>
        s.profile_photo_url_full
        || s.profile_photo_url
        || s.profile_photo
        || s.avatar
        || s.photo_url
        || null;

    /* Headline fallback chain — same logic MyNetwork uses (subtitleFor).
       Falls through to designation + company when the API doesn't surface
       a single `headline` field. */
    const subtitleFor = (s) => {
        if (s.headline) return s.headline;
        const designation = s.current_designation || s.designation;
        const company = s.current_company || s.company;
        if (designation && company) return `${designation} · ${company}`;
        return company || designation || '';
    };

    return (
        <SideWidget title="People you may know">
            <ul className="list-none p-0 m-0">
                {suggestions.slice(0, 5).map((s, idx) => {
                    const id = s.profile_id || s.id;
                    const color = colorFor(id);
                    const mutualCount = s.mutual_count ?? s.mutual_connections_count ?? 0;
                    const subtitle = subtitleFor(s);
                    const locationCity = s.location_city || s.city || s.location || '';
                    /* Combined meta line — same pattern as MyNetwork's PYMK so
                       both widgets read consistently. Joins city + mutual count
                       with a · when both are present. */
                    const meta = [
                        locationCity && `📍 ${locationCity}`,
                        mutualCount > 0 && `👥 ${mutualCount} mutual`,
                    ].filter(Boolean).join('  ·  ');
                    const goToProfile = () => id && navigate(`/app/profile/${id}`);
                    return (
                        <li key={id || idx}>
                            {/* Whole row is the click target → navigates to the
                                agent's profile, where Connect/Message live. */}
                            <div
                                className="flex align-items-center gap-3 py-3 px-1 cursor-pointer hover:surface-50 border-round transition-colors transition-duration-150"
                                role="button"
                                tabIndex={0}
                                onClick={goToProfile}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        goToProfile();
                                    }
                                }}
                            >
                                <Avatar
                                    label={initialsFrom(s.full_name)}
                                    image={photoUrl(s)}
                                    shape="circle"
                                    style={{
                                        width: '3rem',
                                        height: '3rem',
                                        backgroundColor: `var(--${color}-100)`,
                                        color: `var(--${color}-700)`,
                                        fontWeight: 700,
                                        fontSize: '0.8rem',
                                        flexShrink: 0,
                                    }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {s.full_name || 'Unknown'}
                                    </div>
                                    {subtitle && (
                                        <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {subtitle}
                                        </div>
                                    )}
                                    {meta && (
                                        <div className="text-400 text-xs mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {meta}
                                        </div>
                                    )}
                                </div>
                                <i className="pi pi-chevron-right text-400 flex-shrink-0" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                            </div>
                            {idx < Math.min(suggestions.length, 5) - 1 && <Divider className="my-0" />}
                        </li>
                    );
                })}
            </ul>
        </SideWidget>
    );
};

const QuickActionsWidget = ({ navigate }) => {
    const items = [
        { icon: 'pi-users',         label: 'My network',  color: 'blue',   onClick: () => navigate('/app/my-network') },
        { icon: 'pi-envelope',      label: 'Messages',    color: 'cyan',   onClick: () => navigate('/app/messaging') },
        { icon: 'pi-user',          label: 'My profile',  color: 'purple', onClick: () => navigate('/app/my-agent-profile') },
    ];
    return (
        <SideWidget icon="pi-bolt" title="Quick actions">
            <ul className="list-none p-0 m-0">
                {items.map((it, idx) => (
                    <li key={it.label}>
                        <button
                            type="button"
                            onClick={it.onClick}
                            className="w-full flex align-items-center gap-2 py-2 px-2 bg-transparent border-none cursor-pointer text-left border-round hover:surface-50 transition-colors transition-duration-150"
                        >
                            <div
                                className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                style={{ width: '2rem', height: '2rem', backgroundColor: `var(--${it.color}-100)` }}
                            >
                                <i
                                    className={`pi ${it.icon}`}
                                    style={{ color: `var(--${it.color}-600)`, fontSize: '0.8rem' }}
                                    aria-hidden="true"
                                />
                            </div>
                            <span className="text-900 text-sm flex-1">{it.label}</span>
                            <i className="pi pi-chevron-right text-400" style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                        </button>
                        {idx < items.length - 1 && <Divider className="my-0" />}
                    </li>
                ))}
            </ul>
        </SideWidget>
    );
};

// ─── Empty state ────────────────────────────────────────────────────────
const EmptyFeedState = ({ tab }) => {
    const config = {
        0: { icon: 'pi-comments', title: 'No posts yet', desc: 'Connect with more agents and share your first post above.' },
        1: { icon: 'pi-user',     title: "You haven't posted yet", desc: 'Share your first update — the composer is right at the top.' },
        2: { icon: 'pi-bookmark', title: 'Nothing saved', desc: 'Save posts from the feed to read them later.' },
    };
    const c = config[tab] || config[0];
    return (
        <Card
            className="shadow-1 mb-3"
            pt={{ body: { className: 'p-4 md:p-6' }, content: { className: 'p-0' } }}
        >
            <div className="flex flex-column align-items-center text-center gap-3 py-3">
                <div
                    className="flex align-items-center justify-content-center border-circle"
                    style={{ width: '3.5rem', height: '3.5rem', backgroundColor: 'var(--surface-100)' }}
                >
                    <i className={`pi ${c.icon} text-500`} style={{ fontSize: '1.4rem' }} aria-hidden="true" />
                </div>
                <div>
                    <h3 className="text-900 font-semibold m-0 mb-1">{c.title}</h3>
                    <p className="text-600 text-sm m-0" style={{ maxWidth: '24rem' }}>{c.desc}</p>
                </div>
            </div>
        </Card>
    );
};

export default SocialFeed;
